import React, { ReactElement, Fragment } from 'react'
import type { GridBlock as GridBlockProps } from '@/payload-types'
import { Card, CardPerson, CardEquipment, CardResource } from '@/components'

type Item = NonNullable<GridBlockProps['items']>[number]

function itemToCard(item: Item): ReactElement | null {
  if (typeof item.value !== 'object') return null

  switch (item.relationTo) {
    case 'events': {
      const props = {
        id: item.value.id,
        title: item.value.title,
        lead: item.value.lead ?? null,
        image: item.value.image ?? null,
        href: `/events/${item.value.slug}`,
      }
      return <Card {...props} />
    }
    case 'documents': {
      const props = {
        id: item.value.id,
        title: item.value.title,
        lead: item.value.lead ?? null,
        href: `/documents/${item.value.slug}`,
      }
      return <Card {...props} />
    }
    case 'people': {
      return <CardPerson person={item.value} />
    }
    case 'equipments': {
      return <CardEquipment equipment={item.value} />
    }
    case 'resources': {
      return <CardResource resource={item.value} />
    }
    case 'services': {
      const props = {
        id: item.value.id,
        title: item.value.title,
        lead: item.value.lead,
        image: item.value.image,
        href: String(item.value.breadcrumbs?.at(-1)?.url),
      }
      return <Card {...props} />
    }
    case 'posts': {
      const props = {
        id: item.value.id,
        title: item.value.title,
        lead: item.value.lead ?? null,
        image: item.value.image,
        href: `/blog/${item.value.slug}`,
      }
      return <Card {...props} />
    }
    case 'pages': {
      const props = {
        id: item.value.id,
        title: item.value.title,
        lead: item.value.lead ?? null,
        href: String(item.value.breadcrumbs?.at(-1)?.url),
      }
      return <Card key={props.id} {...props} />
    }
    default:
      return null
  }
}

export function GridBlock({ title, lead, items }: Readonly<GridBlockProps>): React.ReactNode {
  return (
    <section className="group flex-1 p-8 [.group+&]:border-t">
      <div className="flex flex-col max-w-7xl mx-auto gap-8">
        {title || lead ? (
          <header className="lg:flex-1 flex flex-col gap-4 self-center text-center">
            {title ? <h2 className="text-lg lg:text-xl font-semibold">{title}</h2> : null}
            {lead ? <p className="text-sm">{lead}</p> : null}
          </header>
        ) : null}

        {items ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => {
              if (typeof item.value === 'number') return null
              return <Fragment key={item.value.id}>{itemToCard(item)}</Fragment>
            })}
          </div>
        ) : null}
      </div>
    </section>
  )
}
